// Catalog.ID Client — Reading and writing the account's ledger instruments
// ============================================================================
// The side-effecting half of `crypto/bitcash-instruments-doc.ts`: one
// compare-and-set document in the vault's `cid` namespace,
// `cid/bitcash-instruments` (apps/bitcash/docs/plans/bitcash-v2.md M12). Every edit is
// read-modify-write; on a lost CAS the edit is RE-APPLIED to what the account
// now holds (the edits are idempotent per code), exactly as
// `state/bitcash-wallets-store.ts` does for the wallet list.
//
// The two writes the money leg needs are `recordFund` and `recordToken`, each
// made BEFORE the ledger call that creates the instrument (write-ahead: the
// ledger never returns a code). Callers treat a failed write as "keep the code
// in the stash and carry on": a print step is never blocked by bookkeeping.

import { readCidBlob, writeCidBlob, type CidBlobDeps } from './cid-blobs';
import { BlobConflictError } from '../api/blobs';
import {
    BITCASH_INSTRUMENTS_BLOB, decodeInstrumentsDocument, emptyInstrumentsDocument, withFund, withToken,
    type BitcashInstrumentsDocument, type InstrumentToken,
} from '../crypto/bitcash-instruments-doc';

const MAX_ATTEMPTS = 3;

export interface InstrumentsStoreDeps {
    read(): Promise<{ value: unknown; generation: number } | null>;
    write(doc: BitcashInstrumentsDocument, ifGeneration: number): Promise<{ generation: number }>;
}

export function instrumentsStoreDeps(blobs?: CidBlobDeps): InstrumentsStoreDeps {
    return {
        read: () => readCidBlob<unknown>(BITCASH_INSTRUMENTS_BLOB, blobs),
        write: (doc, ifGeneration) => writeCidBlob(BITCASH_INSTRUMENTS_BLOB, doc, ifGeneration, blobs),
    };
}

export const defaultInstrumentsStoreDeps: InstrumentsStoreDeps = instrumentsStoreDeps();

export interface InstrumentsSnapshot {
    doc: BitcashInstrumentsDocument;
    generation: number;
}

/** The account's instruments, or null when it has none yet (a normal answer). */
export async function loadInstruments(
    deps: InstrumentsStoreDeps = defaultInstrumentsStoreDeps,
): Promise<InstrumentsSnapshot | null> {
    const row = await deps.read();
    if (row === null) return null;
    return { doc: decodeInstrumentsDocument(row.value), generation: row.generation };
}

/** Read, apply `mutate` (pure), write — retrying on a lost CAS so the edit lands on the latest document. */
export async function mutateInstruments(
    mutate: (doc: BitcashInstrumentsDocument) => BitcashInstrumentsDocument,
    deps: InstrumentsStoreDeps = defaultInstrumentsStoreDeps,
): Promise<InstrumentsSnapshot> {
    let lastConflict: BlobConflictError | null = null;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const current = await loadInstruments(deps);
        const base = current?.doc ?? emptyInstrumentsDocument();
        const next = mutate(base);
        if (current !== null && next === base) return current;   // nothing to write: a retry
        try {
            const { generation } = await deps.write(next, current?.generation ?? 0);
            return { doc: next, generation };
        } catch (err) {
            if (!(err instanceof BlobConflictError)) throw err;
            lastConflict = err;
        }
    }
    throw new Error('Your bitcash instruments are being changed on another device — nothing was saved. '
        + `Try again. (${lastConflict?.message ?? 'conflict'})`);
}

const nowSeconds = (): number => Math.floor(Date.now() / 1000);

/** Write down a receive address of the wallet `walletRef` BEFORE binding it on the ledger. */
export function recordFund(walletRef: string, code: string, deps?: InstrumentsStoreDeps): Promise<InstrumentsSnapshot> {
    return mutateInstruments(doc => withFund(doc, walletRef, code, nowSeconds()), deps);
}

/** Write down a token of the wallet `walletRef` BEFORE creating it on the ledger. */
export function recordToken(
    walletRef: string, token: Omit<InstrumentToken, 'createdAt'>, deps?: InstrumentsStoreDeps,
): Promise<InstrumentsSnapshot> {
    return mutateInstruments(doc => withToken(doc, walletRef, { ...token, createdAt: nowSeconds() }), deps);
}
